import { faCheck, faCopy, faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import CIcon from "@coreui/icons-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactTooltip from "react-tooltip";
import { cilFingerprint } from "@coreui/icons";
import classNames from "classnames";
import styles from "./attachment.module.css";

const Attachment = ({ filename, focus, hash, needleId, onClick, size, title, disabled=false }) => {
  const [hasCopied, setHasCopied] = useState(false);
  const fileExt = filename.split(".").slice(-1).pop().toLowerCase();
  const sha256 = hash.find(h => h.algorithm === "sha256")?.value ?? "";

  let friendlySize = size;
  let units = ["bytes", "KB", "MB", "GB", "TB"];
  while (friendlySize >= 1024 && units.length > 1) {
    friendlySize = ~~(friendlySize / 102.4) / 10;
    units.shift();
  }
  friendlySize += "\u00A0" + units[0];

  const ref = useRef();
  useEffect(() => {
    if (ref.current && focus) {
      ref.current.focus();
    }
  }, [focus, ref]);

  const handleCopyClick = async e => {
    e.stopPropagation();
    setHasCopied(true);
    await navigator.clipboard.writeText(sha256);
    setTimeout(() => setHasCopied(false), 1000);
  };

  const tooltip = (
    <ReactTooltip
      className="mw-100"
      clickable
      effect="solid"
      globalEventOff="click"
      id={needleId}
      overridePosition={({ left, top }, currentEvent, currentTarget, node) => {
        const d = document.documentElement;
        left = Math.min(d.clientWidth - node.clientWidth, left);
        top = Math.min(d.clientHeight - node.clientHeight, top);
        left = Math.max(0, left);
        top = Math.max(0, top);
        return { top, left };
      }}
    >
      <div onClick={e => e.stopPropagation()}>
        <div>Filename: {filename}</div>
        {size ? <div>Size: {friendlySize}</div> : null}
        {sha256 ? (
          <div className="align-items-center d-flex flex-wrap">
            <CIcon className="fa-w-14 fa-fw mr-1" icon={cilFingerprint} />
            SHA256:&nbsp;
            <samp className="font-weight-bold mr-2" style={{ wordBreak: "break-all" }}>
              {sha256}
            </samp>
            <button
              aria-label="Copy hash to clipboard"
              className={styles.btnCopy}
              onClick={handleCopyClick}
              title="Copy hash to clipboard"
            >
              <FontAwesomeIcon fixedWidth icon={hasCopied ? faCheck : faCopy} />
            </button>
          </div>
        ) : null}
      </div>
    </ReactTooltip>
  );

  
  return (
    <>
      <div
        className={classNames(styles.needle, "d-flex align-items-center mb-0 py-2 rounded")}
        id={`needle-${needleId}`}
        tabIndex="0"
        ref={ref}
      >
        <button 
           className={classNames(styles.filename, "text-truncate")} 
           onClick={(e) => {
             if (!disabled) onClick(e);
           }} 
           title={disabled ? '' : title} 
           style={disabled ? {textDecoration: "none", cursor: 'auto'} : {}}
        >
          {filename}
        </button>
        &nbsp;
        <span className="mr-2">({friendlySize})</span>
        <button
          className={classNames(styles.btnSm, "mr-2")}
          data-for={needleId}
          data-tip
          data-event="click"
          data-iscapture="true"
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faEllipsisH} />
        </button>
        <span className={classNames(styles.ext, "d-flex ml-auto px-1 py-0 rounded text-uppercase user-select-none")}>
          {fileExt}
        </span>
      </div>
      {tooltip}
    </>
  );
};

export default Attachment;
